import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Wrench, Loader2, FileDown, Trash2, Building2, DoorClosed, User,
  CheckCircle2, AlertTriangle, Package, Camera, MapPin, FileText, ShieldCheck,
  Play, ClipboardCheck, Navigation, ExternalLink,
} from "lucide-react";
import { maintenanceApi, engineersApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, fmtDateTime, apiError, statusTone, prettyStatus, fmtMoney } from "@/lib/format";
import { buildJobAddress, directionsUrl } from "@/lib/directions";
import { useAuth, userHasRole } from "@/context/AuthContext";
import { generateMaintenanceReportForJob } from "@/pdf/webMaintenanceReport";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { CompleteJobDialog } from "@/components/maintenance/CompleteJobDialog";
import { EmergencyCompleteDialog } from "@/components/maintenance/EmergencyCompleteDialog";
import { ReinspectionPrompt } from "@/components/maintenance/ReinspectionPrompt";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function BackLink() {
  return (
    <Link to="/maintenance" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Maintenance
    </Link>
  );
}

function PhotoStrip({ photos, testId }) {
  if (!Array.isArray(photos) || photos.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid={testId}>
      {photos.map((p, i) => (
        <img key={`${testId}-${i}`} src={p} alt={`evidence ${i + 1}`} className="h-24 w-32 rounded-lg border border-slate-200 object-cover" />
      ))}
    </div>
  );
}

export default function MaintenanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.is_company_admin || userHasRole(user, "admin", "inspector");
  const { data: job, loading, error, reload } = useAsync(() => maintenanceApi.getOne(id), [id]);
  const engineers = useAsync(() => (canManage ? engineersApi.list() : Promise.resolve({ data: [] })), [canManage]);

  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [reinspectOpen, setReinspectOpen] = useState(false);

  if (loading) return <div><BackLink /><LoadingState label="Loading job…" /></div>;
  if (error) return <div><BackLink /><ErrorState message={error} onRetry={reload} /></div>;
  if (!job) return null;

  const c = job.completion || {};
  const door = job.door || {};
  const engList = Array.isArray(engineers.data) ? engineers.data : [];

  const patch = async (data, msg) => {
    setBusy(true);
    try { await maintenanceApi.update(id, data); toast.success(msg || "Saved"); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not update")); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this maintenance job? This cannot be undone.")) return;
    setBusy(true);
    try { await maintenanceApi.remove(id); toast.success("Job deleted"); navigate("/maintenance"); }
    catch (e) { toast.error(apiError(e, "Could not delete")); setBusy(false); }
  };

  const doReport = async () => {
    setPdfBusy(true);
    try { await generateMaintenanceReportForJob(job); toast.success("Completion report generated"); }
    catch (e) { toast.error(apiError(e, "Could not generate report")); }
    finally { setPdfBusy(false); }
  };

  const doStart = async () => {
    setBusy(true);
    try { await maintenanceApi.start(id); toast.success("Job started"); await reload(); }
    catch (e) { toast.error(apiError(e, "Could not start job")); }
    finally { setBusy(false); }
  };

  const isCompleted = job.status === "completed";

  return (
    <div>
      <BackLink />
      <PageHeader
        title={job.title || job.door_reference || "Maintenance job"}
        subtitle={[job.building_name, job.door_reference].filter(Boolean).join(" · ")}
        testId="maintenance-detail-header"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={statusTone(job.status)}>{prettyStatus(job.status)}</Badge>
            {!isCompleted && job.status !== "in_progress" && (
              <Button variant="outline" size="sm" disabled={busy} onClick={doStart} data-testid="maintenance-start-btn">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Start job
              </Button>
            )}
            {!isCompleted && (
              <Button size="sm" onClick={() => (job.is_emergency ? setEmergencyOpen(true) : setCompleteOpen(true))} data-testid="maintenance-complete-btn">
                <ClipboardCheck className="mr-1.5 h-4 w-4" /> Complete job
              </Button>
            )}
            {isCompleted && (
              <Button variant="outline" size="sm" disabled={pdfBusy} onClick={doReport} data-testid="maintenance-report-btn">
                {pdfBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} Completion report
              </Button>
            )}
            {canManage && (
              <Button variant="outline" size="icon" disabled={busy} onClick={doDelete} data-testid="maintenance-delete-btn" className="text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Required repairs / parts */}
          <Card className="p-5">
            <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><Wrench className="h-5 w-5 text-primary" /> Required works</h3>
            {(job.items || []).length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">No specific parts listed on this job.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="maintenance-items">
                {job.items.map((it, i) => (
                  <li key={`${it.component || "item"}-${i}`} className="flex items-start justify-between gap-3 py-2.5">
                    <div>
                      <p className="font-medium text-slate-800">{it.component}{it.quantity > 1 ? ` ×${it.quantity}` : ""}</p>
                      {it.description && <p className="text-sm text-slate-500">{it.description}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {it.auto_derived_from_inspection && <Badge variant="outline" className="bg-blue-50 text-blue-700">From inspection</Badge>}
                      {Number(it.cost) > 0 && (
                        <span className="text-sm font-medium text-slate-700" data-testid={`maintenance-item-cost-${i}`}>
                          {fmtMoney(Number(it.cost) * (it.quantity || 1))}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {Number(job.total) > 0 && (
              <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-sm font-semibold text-slate-800" data-testid="maintenance-items-total">
                <span>Estimated total</span>
                <span>{fmtMoney(job.total)}</span>
              </div>
            )}
          </Card>

          {/* Completion evidence */}
          {isCompleted && (
            <Card className="p-5" data-testid="maintenance-completion">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Work carried out</h3>
              {c.work_done && <p className="mb-3 whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{c.work_done}</p>}
              {Array.isArray(c.item_completions) && c.item_completions.length > 0 && (
                <div className="space-y-3">
                  {c.item_completions.map((ic, i) => (
                    <div key={`${ic.component || "comp"}-${i}`} className={`rounded-lg border-l-4 bg-slate-50 p-3 ${ic.fixed ? "border-emerald-500" : "border-amber-500"}`}>
                      <p className={`text-sm font-semibold ${ic.fixed ? "text-emerald-700" : "text-amber-700"}`}>{ic.fixed ? "✓" : "⚠"} {ic.component}</p>
                      {ic.notes && <p className="mt-0.5 text-sm text-slate-600">{ic.notes}</p>}
                      <PhotoStrip photos={ic.photos} testId={`item-photos-${i}`} />
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(c.materials) && c.materials.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Package className="h-4 w-4" /> Materials used</h4>
                  <div className="space-y-2">
                    {c.materials.map((m, i) => (
                      <div key={`${m.name || "mat"}-${i}`} className="rounded-lg border border-slate-200 p-3">
                        <p className="text-sm font-medium text-slate-800">{m.name}</p>
                        {m.notes && <p className="text-sm text-slate-500">{m.notes}</p>}
                        <PhotoStrip photos={m.photos} testId={`material-photos-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(c.photos) && c.photos.length > 0 && (
                <div className="mt-4">
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Camera className="h-4 w-4" /> Photos of completed works</h4>
                  <PhotoStrip photos={c.photos} testId="completion-photos" />
                </div>
              )}
              {c.signature && (
                <p className="mt-4 border-t border-dashed border-slate-200 pt-3 text-sm text-slate-600">
                  Signed by engineer: <span className="font-semibold italic text-slate-800">{c.signature}</span>
                  {job.completed_at ? ` · ${fmtDateTime(job.completed_at)}` : ""}
                </p>
              )}
            </Card>
          )}

          {/* Post-work inspection */}
          {job.post_work_inspection && (
            <Card className="border-emerald-200 bg-emerald-50/50 p-5" data-testid="maintenance-postwork">
              <h3 className="flex items-center gap-2 font-semibold text-emerald-800"><ShieldCheck className="h-5 w-5" /> Verified by post-work inspection</h3>
              <p className="mt-1 text-sm text-emerald-900">
                Re-inspected {fmtDate(job.post_work_inspection.inspection_date)}
                {job.post_work_inspection.inspector_name ? ` by ${job.post_work_inspection.inspector_name}` : ""} — outcome{" "}
                <strong>{prettyStatus(job.post_work_inspection.status)}</strong>.
              </p>
              {job.post_work_inspection.inspection_id && (
                <Link to={`/inspections/${job.post_work_inspection.inspection_id}`} className="mt-2 inline-block text-sm font-medium text-emerald-700 hover:underline">View inspection →</Link>
              )}
            </Card>
          )}

          {/* External quote */}
          {job.external_quote_ref && (
            <Card className="p-5">
              <h3 className="mb-2 flex items-center gap-2 font-semibold text-slate-800"><FileText className="h-5 w-5 text-primary" /> External quote</h3>
              <p className="text-sm text-slate-600">Ref: {job.external_quote_ref}{job.external_quote_amount ? ` · £${job.external_quote_amount}` : ""}</p>
              {job.external_quote_notes && <p className="mt-1 text-sm text-slate-500">{job.external_quote_notes}</p>}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {(() => {
            const addr = buildJobAddress(job);
            const dir = directionsUrl(addr);
            if (!addr) return null;
            return (
              <Card className="p-4">
                <a
                  href={dir}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex items-center gap-3"
                  data-testid="job-detail-directions"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                    <Navigation className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 group-hover:underline">Directions</p>
                    <p className="text-sm text-slate-500">{addr}</p>
                  </div>
                  <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
                </a>
              </Card>
            );
          })()}

          <Card className="p-5">
            <h3 className="mb-3 font-semibold text-slate-800">Details</h3>
            <dl className="space-y-2.5 text-sm">
              <Row label="Status" value={<Badge variant="outline" className={statusTone(job.status)}>{prettyStatus(job.status)}</Badge>} />
              <Row label="Priority" value={<span className="capitalize">{job.priority === "urgent" ? <span className="inline-flex items-center gap-1 font-semibold text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> Urgent</span> : job.priority}</span>} />
              {job.building_id && <Row label="Building" value={<Link to={`/buildings/${job.building_id}`} className="inline-flex items-center gap-1 text-primary hover:underline"><Building2 className="h-3.5 w-3.5" />{job.building_name || "View"}</Link>} />}
              {door.id && <Row label="Door" value={<Link to={`/doors/${door.id}`} className="inline-flex items-center gap-1 text-primary hover:underline"><DoorClosed className="h-3.5 w-3.5" />{door.door_reference || "View"}</Link>} />}
              <Row label="Engineer" value={<span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5 text-slate-400" />{job.engineer_name || "Unassigned"}</span>} />
              <Row label="Scheduled" value={fmtDate(job.scheduled_for)} />
              {job.completed_at && <Row label="Completed" value={fmtDateTime(job.completed_at)} />}
              {job.access_details && <Row label="Access" value={job.access_details} />}
              {job.location_address && <Row label="Address" value={<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-slate-400" />{job.location_address}</span>} />}
            </dl>
          </Card>

          {/* Admin controls */}
          {canManage && job.status !== "completed" && (
            <Card className="p-5">
              <h3 className="mb-3 font-semibold text-slate-800">Manage</h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Assign engineer</Label>
                  <Select value={job.engineer_id || "none"} onValueChange={(v) => patch({ engineer_id: v === "none" ? null : v }, "Engineer updated")} disabled={busy}>
                    <SelectTrigger data-testid="assign-engineer-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {engList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={job.priority || "normal"} onValueChange={(v) => patch({ priority: v }, "Priority updated")} disabled={busy}>
                    <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["urgent", "normal", "low"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Scheduled date</Label>
                  <Input type="date" defaultValue={(job.scheduled_for || "").slice(0, 10)} onBlur={(e) => e.target.value && patch({ scheduled_for: e.target.value }, "Schedule updated")} disabled={busy} data-testid="schedule-input" />
                </div>
                {door.id && (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid="requires-reinspection-row">
                    <div className="min-w-0">
                      <Label className="flex items-center gap-1.5 text-sm"><ShieldCheck className="h-4 w-4 text-slate-500" /> Requires post-work re-inspection</Label>
                      <p className="mt-0.5 text-xs text-slate-500">Prompt the engineer to re-inspect (or request an inspector) on completion.</p>
                    </div>
                    <Switch
                      checked={!!job.requires_reinspection}
                      onCheckedChange={(v) => patch({ requires_reinspection: v }, v ? "Re-inspection required on completion" : "Re-inspection requirement removed")}
                      disabled={busy}
                      data-testid="requires-reinspection-toggle"
                    />
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Arrivals log */}
          {Array.isArray(job.arrivals) && job.arrivals.length > 0 && (
            <Card className="p-5">
              <h3 className="mb-2 flex items-center gap-2 font-semibold text-slate-800"><MapPin className="h-4 w-4 text-primary" /> Site arrivals</h3>
              <ul className="space-y-1.5 text-sm text-slate-600">
                {job.arrivals.map((a) => (
                  <li key={a.id} className="flex items-center justify-between">
                    <span>{a.engineer_name || "Engineer"} <span className="text-slate-400">· {a.via}</span></span>
                    <span className="text-slate-400">{fmtDateTime(a.ts)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <CompleteJobDialog
        jobId={id}
        items={job.items || []}
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        onDone={async (result) => {
          setCompleteOpen(false);
          await reload();
          if (result?.requires_reinspection && door.id) setReinspectOpen(true);
        }}
      />

      <EmergencyCompleteDialog
        jobId={id}
        open={emergencyOpen}
        onOpenChange={setEmergencyOpen}
        onDone={() => { setEmergencyOpen(false); reload(); }}
      />

      <ReinspectionPrompt
        jobId={id}
        doorId={door.id}
        doorRef={door.door_reference}
        open={reinspectOpen}
        onOpenChange={setReinspectOpen}
        onDone={reload}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-800">{value}</dd>
    </div>
  );
}
